import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ScoreBadge from "@/components/ScoreBadge";
import ResumePreviewDialog from "@/components/ResumePreviewDialog";
import { Upload, History, Eye, User } from "lucide-react";

export default function CandidateVersions({ candidateId, onUpdated }) {
    const [versions, setVersions] = useState([]);
    const [current, setCurrent] = useState(null);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null); // {versionId, filename}
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get(`/candidates/${candidateId}/resume-versions`);
            setVersions(data.versions || []);
            setCurrent(data.current || null);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [candidateId]);

    const upload = async (files) => {
        if (!files || !files.length) return;
        setBusy(true);
        const fd = new FormData();
        fd.append("file", files[0]);
        try {
            const { data } = await api.post(`/candidates/${candidateId}/resume-versions`, fd, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
            });
            toast.success(`Resume replaced · ${data.new_skills_added?.length || 0} new skill(s) · ${data.rescored} job match(es) re-scored`);
            if (fileRef.current) fileRef.current.value = "";
            await load();
            onUpdated && onUpdated();
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-display font-bold text-xl">Resume versions</h3>
                    <p className="text-sm text-foreground/80 mt-1">Each version shows the best AI match score it achieved across all assigned jobs.</p>
                </div>
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt" hidden onChange={(e) => upload(e.target.files)} data-testid="version-upload-input" />
                <Button onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="upload-new-version">
                    <Upload className="w-4 h-4 mr-2" /> {busy ? "Processing..." : "Upload new version"}
                </Button>
            </div>

            {versions.length === 0 ? (
                <div className="border border-dashed border-border bg-white p-8 text-center text-sm text-foreground/80">No resume on file yet.</div>
            ) : (
                <ul className="border border-border bg-white divide-y divide-border" data-testid="versions-list">
                    {versions.map((v, idx) => {
                        const isCurrent = v.is_current || v.id === "current";
                        return (
                            <li key={v.id || idx} className="p-4 text-sm flex flex-col gap-2" data-testid={`version-row-${v.id}`}>
                                <div className="flex items-center justify-between flex-wrap gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        {isCurrent ? (
                                            <span className="text-[10px] font-mono uppercase tracking-widest bg-ikb text-white px-2 py-0.5 shrink-0">Current</span>
                                        ) : (
                                            <History className="w-3.5 h-3.5 text-foreground/60 shrink-0" />
                                        )}
                                        <span className="font-medium truncate">{v.filename || "—"}</span>
                                        {!isCurrent && v.archived_at && (
                                            <span className="text-xs text-foreground/80 font-mono">archived {new Date(v.archived_at).toLocaleString()}</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        {v.match_score != null && v.match_score > 0 && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/80">Best match</span>
                                                <ScoreBadge score={v.match_score} size="sm" />
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => setPreview({ versionId: v.id || "current", filename: v.filename || current })}
                                            className="text-xs font-mono uppercase tracking-widest border border-border px-2 py-1.5 hover:bg-secondary inline-flex items-center gap-1"
                                            data-testid={`version-preview-${v.id}`}
                                        >
                                            <Eye className="w-3 h-3" /> Preview
                                        </button>
                                    </div>
                                </div>
                                {(v.uploaded_by_name || v.uploaded_at) && (
                                    <div className="flex items-center gap-2 text-[11px] font-mono text-foreground/70 pl-6" data-testid={`version-uploader-${v.id}`}>
                                        <User className="w-3 h-3" />
                                        <span>Uploaded by <strong className="text-foreground">{v.uploaded_by_name || "Unknown"}</strong></span>
                                        {v.uploaded_by_role && (
                                            <span className="uppercase tracking-widest border border-border px-1.5 py-px text-[9px]">{v.uploaded_by_role}</span>
                                        )}
                                        {v.uploaded_at && (
                                            <span>· {new Date(v.uploaded_at).toLocaleString()}</span>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {preview && (
                <ResumePreviewDialog
                    open={!!preview}
                    onClose={() => setPreview(null)}
                    candidateId={candidateId}
                    versionId={preview.versionId}
                    filename={preview.filename}
                />
            )}
        </div>
    );
}
